import {
  AirHandlerProductType,
  CondenserProductType,
  ValidSystemType
} from 'data/types';

const MAX_AIR_HANDLER_SLOTS = 10;

const getComponentParts = (fs: ValidSystemType): string[] => {
  const parts = [String(fs.condenserProductId)];
  for (let n = 1; n <= MAX_AIR_HANDLER_SLOTS; n += 1) {
    const pid = fs[`airHandler${n}ProductId` as keyof ValidSystemType];
    parts.push(String(pid ?? 'null'));
  }
  return parts;
};

export const getComponentFingerprint = (fs: ValidSystemType): string =>
  getComponentParts(fs).join('|');

// Exported for swap-option-derivation, which must not import utils/systems
// (its app/store import drags untransformable ESM deps into Jest).
export const getSwapSlot = (
  current: ValidSystemType,
  type: 'condenser' | 'airHandler',
  zoneId?: string
): number => {
  if (type === 'condenser') return 0;
  if (!zoneId) return -1;
  for (let n = 1; n <= MAX_AIR_HANDLER_SLOTS; n += 1) {
    const uniqueKey = `airHandler${n}Unique` as keyof ValidSystemType;
    if (current[uniqueKey] === zoneId) return n;
  }
  return -1;
};

export function findSwapTargetIndex(
  systems: ValidSystemType[],
  current: ValidSystemType,
  type: 'condenser' | 'airHandler',
  productId: number,
  zoneId?: string
): number {
  const slot = getSwapSlot(current, type, zoneId);
  if (slot === -1) return -1;

  const currentParts = getComponentParts(current);
  const targetParts = [...currentParts];
  targetParts[slot] = String(productId);
  const targetFingerprint = targetParts.join('|');

  return systems.findIndex(
    (s) => getComponentFingerprint(s) === targetFingerprint
  );
}

export type SwapLocks = {
  condenserLocked: boolean;
  lockedZoneIds: string[]; // zone uniqueIds whose current AH must be preserved
};

export type SwapChange = {
  slot: number; // 0 = condenser, 1..10 = air handler slot
  zoneId: string | null; // airHandlerNUnique for AH slots, null for condenser
  fromProductId: number | null;
  toProductId: number | null;
};

export type SwapResolution = {
  index: number; // index into the systems array
  system: ValidSystemType;
  changes: SwapChange[]; // EXCLUDES the clicked slot itself; empty = exact swap
};

const getAirHandlerProductForZone = (
  fs: ValidSystemType,
  zoneId: string
): number | null => {
  const slot = getSwapSlot(fs, 'airHandler', zoneId);
  if (slot === -1) return null;
  const pid = fs[`airHandler${slot}ProductId` as keyof ValidSystemType];
  return (pid as number | null | undefined) ?? null;
};

const collectZoneIds = (
  current: ValidSystemType,
  candidate: ValidSystemType
): string[] => {
  const zoneIds: string[] = [];
  [current, candidate].forEach((fs) => {
    for (let n = 1; n <= MAX_AIR_HANDLER_SLOTS; n += 1) {
      const unique = fs[`airHandler${n}Unique` as keyof ValidSystemType];
      if (typeof unique === 'string' && !zoneIds.includes(unique)) {
        zoneIds.push(unique);
      }
    }
  });
  return zoneIds;
};

const computeChanges = (
  current: ValidSystemType,
  candidate: ValidSystemType,
  type: 'condenser' | 'airHandler',
  clickedZoneId: string | undefined
): SwapChange[] => {
  const changes: SwapChange[] = [];

  if (
    type !== 'condenser' &&
    candidate.condenserProductId !== current.condenserProductId
  ) {
    changes.push({
      slot: 0,
      zoneId: null,
      fromProductId: current.condenserProductId ?? null,
      toProductId: candidate.condenserProductId ?? null
    });
  }

  collectZoneIds(current, candidate).forEach((zoneId) => {
    if (type === 'airHandler' && zoneId === clickedZoneId) return;
    const fromProductId = getAirHandlerProductForZone(current, zoneId);
    const toProductId = getAirHandlerProductForZone(candidate, zoneId);
    if (fromProductId === toProductId) return;
    // Slot numbering follows the current system; fall back to the
    // candidate's slot for zones the current system doesn't have.
    const currentSlot = getSwapSlot(current, 'airHandler', zoneId);
    const slot =
      currentSlot !== -1
        ? currentSlot
        : getSwapSlot(candidate, 'airHandler', zoneId);
    changes.push({ slot, zoneId, fromProductId, toProductId });
  });

  return changes;
};

const scoreOf = (s: ValidSystemType): number => s.score?.total ?? 0;

const candidateHoldsClickedProduct = (
  candidate: ValidSystemType,
  type: 'condenser' | 'airHandler',
  productId: number,
  zoneId: string | undefined
): boolean => {
  if (type === 'condenser') return candidate.condenserProductId === productId;
  if (!zoneId) return false;
  return getAirHandlerProductForZone(candidate, zoneId) === productId;
};

const candidateSatisfiesLocks = (
  candidate: ValidSystemType,
  current: ValidSystemType,
  type: 'condenser' | 'airHandler',
  clickedZoneId: string | undefined,
  locks: SwapLocks
): boolean => {
  // Clicking a condenser IS the condenser change, so the condenser lock
  // must not deadlock the resolver in that case.
  if (
    type !== 'condenser' &&
    locks.condenserLocked &&
    candidate.condenserProductId !== current.condenserProductId
  ) {
    return false;
  }
  return locks.lockedZoneIds.every((zoneId) => {
    if (type === 'airHandler' && zoneId === clickedZoneId) return true;
    return (
      getAirHandlerProductForZone(candidate, zoneId) ===
      getAirHandlerProductForZone(current, zoneId)
    );
  });
};

export function resolveSwap(
  systems: ValidSystemType[],
  current: ValidSystemType,
  type: 'condenser' | 'airHandler',
  productId: number,
  zoneId: string | undefined,
  locks: SwapLocks
): SwapResolution | null {
  const exactIndex = findSwapTargetIndex(
    systems,
    current,
    type,
    productId,
    zoneId
  );
  if (exactIndex !== -1) {
    return { index: exactIndex, system: systems[exactIndex], changes: [] };
  }

  let best:
    | (SwapResolution & { manufacturerMatch: boolean; score: number })
    | null = null;

  systems.forEach((candidate, index) => {
    if (!candidateHoldsClickedProduct(candidate, type, productId, zoneId)) {
      return;
    }
    if (!candidateSatisfiesLocks(candidate, current, type, zoneId, locks)) {
      return;
    }

    const changes = computeChanges(current, candidate, type, zoneId);
    const manufacturerMatch = candidate.manufacturer === current.manufacturer;
    const score = scoreOf(candidate);

    const isBetter =
      best === null ||
      changes.length < best.changes.length ||
      (changes.length === best.changes.length &&
        (manufacturerMatch !== best.manufacturerMatch
          ? manufacturerMatch
          : score > best.score));

    if (isBetter) {
      best = { index, system: candidate, changes, manufacturerMatch, score };
    }
  });

  if (!best) return null;
  const { index, system, changes } = best as SwapResolution;
  return { index, system, changes };
}

export function describeSwapChanges(
  changes: SwapChange[],
  products: {
    condensers: CondenserProductType[];
    airHandlers: AirHandlerProductType[];
  },
  zoneNames: Map<string, string>
): string {
  const describeProduct = (
    list: { productId: number; model: string }[],
    productId: number | null
  ): string => {
    if (productId === null) return 'none';
    const product = list.find((p) => p.productId === productId);
    return product?.model ?? String(productId);
  };

  return changes
    .map((change) => {
      if (change.slot === 0) {
        const from = describeProduct(products.condensers, change.fromProductId);
        const to = describeProduct(products.condensers, change.toProductId);
        return `Condenser changed: ${from} → ${to}`;
      }
      const zoneName =
        (change.zoneId != null ? zoneNames.get(change.zoneId) : undefined) ??
        `Zone ${change.slot}`;
      const from = describeProduct(products.airHandlers, change.fromProductId);
      const to = describeProduct(products.airHandlers, change.toProductId);
      return `Zone "${zoneName}" air handler changed: ${from} → ${to}`;
    })
    .join('\n');
}
